import React, { useState, useEffect } from 'react'
import { Button } from '@/components/ui/button'
import { Conversation } from '@/lib/types'
import { Plus, MessageSquare, Trash2, Edit2 } from 'lucide-react'
import { configService } from '@/lib/config-service'
import { RenameConversationDialog } from '@/components/RenameConversationDialog'

interface SidebarProps {
  conversations: Conversation[]
  selectedConversationId?: number
  onSelectConversation: (id: number) => void
  onNewConversation: () => void
  onDeleteConversation: (id: number) => void
  onRenameConversation: (id: number, title: string) => void
}

export function Sidebar({
  conversations,
  selectedConversationId,
  onSelectConversation,
  onNewConversation,
  onDeleteConversation,
  onRenameConversation,
}: SidebarProps) {
  const [templateLibraryEnabled, setTemplateLibraryEnabled] = useState(true)
  const [chatShareEnabled, setChatShareEnabled] = useState(false)
  const [renameDialogOpen, setRenameDialogOpen] = useState(false)
  const [conversationToRename, setConversationToRename] = useState<Conversation | null>(null)

  useEffect(() => {
    // Clear cache to ensure fresh config
    configService.clearCache()

    configService
      .isTemplateLibraryEnabled()
      .then(setTemplateLibraryEnabled)
      .catch(() => {
        // Default to enabled if config fetch fails
        setTemplateLibraryEnabled(true)
      })

    configService
      .isChatShareEnabled()
      .then(setChatShareEnabled)
      .catch(() => {
        // Default to disabled if config fetch fails
        setChatShareEnabled(false)
      })
  }, [])

  // Split conversations into personal and shared groups (when feature enabled)
  const personalConversations = chatShareEnabled
    ? conversations.filter(c => !c.is_shared)
    : conversations

  const sharedConversations = chatShareEnabled
    ? conversations.filter(c => c.is_shared)
    : []

  return (
    <div className="flex flex-col h-full bg-gradient-to-b from-gray-50 to-white dark:from-gray-900 dark:to-gray-800 border-r shadow-sm">
      {/* Header */}
      <div className="p-4 border-b bg-white/50 dark:bg-gray-800/50">
        <Button 
          onClick={onNewConversation} 
          className="w-full bg-gradient-to-r from-emerald-600 to-green-600 hover:from-emerald-700 hover:to-green-700 text-white shadow-md transition-all"
        >
          <Plus className="w-4 h-4 mr-2" />
          New Conversation
        </Button>
        {templateLibraryEnabled && (
          <div className="mt-2">
            <Button
              variant="outline"
              className="w-full"
              onClick={() => { window.location.href = '/library' }}
            >
              Template Library
            </Button>
          </div>
        )}
      </div>

      {/* Conversations List */}
      <div className="flex-1 overflow-y-auto overflow-x-hidden p-2 custom-scrollbar">
        {conversations.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full text-center px-4">
            <MessageSquare className="w-12 h-12 text-gray-300 dark:text-gray-600 mb-3" />
            <p className="text-sm text-muted-foreground">No conversations yet</p>
            <p className="text-xs text-muted-foreground mt-1">Start a new chat to begin</p>
          </div>
        ) : (
          <div className="space-y-4">
            {/* Personal chats */}
            <div>
              {chatShareEnabled && (
                <p className="px-2 mb-1 text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                  Personal chats
                </p>
              )}
              <div className="space-y-1">
                {personalConversations.map((conversation) => (
                  <div
                    key={conversation.id}
                    className={`group flex items-center p-3 rounded-lg cursor-pointer transition-all duration-200 ${
                      selectedConversationId === conversation.id
                        ? 'bg-gradient-to-r from-emerald-500 to-green-500 text-white shadow-md scale-[1.02]'
                        : 'hover:bg-white dark:hover:bg-gray-700 hover:shadow-sm'
                    }`}
                    onClick={() => onSelectConversation(conversation.id)}
                  >
                    <MessageSquare className="w-4 h-4 mr-3 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">
                        {conversation.title}
                      </p>
                      <p className={`text-xs ${
                        selectedConversationId === conversation.id 
                          ? 'text-white/80' 
                          : 'text-muted-foreground'
                      }`}>
                        {conversation.message_count || 0} messages
                      </p>
                    </div>
                    <div className="flex items-center space-x-1 opacity-0 group-hover:opacity-100 transition-opacity">
                      <Button
                        variant="ghost"
                        size="icon"
                        className={`h-7 w-7 ${
                          selectedConversationId === conversation.id
                            ? 'hover:bg-white/20 text-white'
                            : 'hover:bg-gray-200 dark:hover:bg-gray-600'
                        }`}
                        onClick={(e) => {
                          e.stopPropagation()
                          setConversationToRename(conversation)
                          setRenameDialogOpen(true)
                        }}
                      >
                        <Edit2 className="w-3 h-3" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className={`h-7 w-7 ${
                          selectedConversationId === conversation.id
                            ? 'hover:bg-white/20 text-white'
                            : 'hover:bg-red-100 dark:hover:bg-red-900/30 hover:text-red-600'
                        }`}
                        onClick={(e) => {
                          e.stopPropagation()
                          onDeleteConversation(conversation.id)
                        }}
                      >
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Shared chats */}
            {chatShareEnabled && sharedConversations.length > 0 && (
              <div>
                <p className="px-2 mb-1 text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                  Shared chats
                </p>
                <div className="space-y-1">
                  {sharedConversations.map((conversation) => (
                    <div
                      key={conversation.id}
                      className={`group flex items-center p-3 rounded-lg cursor-pointer transition-all duration-200 border-l-4 ${
                        selectedConversationId === conversation.id
                          ? 'bg-gradient-to-r from-emerald-500 to-green-500 text-white shadow-md scale-[1.02] border-emerald-300'
                          : 'hover:bg-white dark:hover:bg-gray-700 hover:shadow-sm border-emerald-400/60 dark:border-emerald-500/60'
                      }`}
                      onClick={() => onSelectConversation(conversation.id)}
                    >
                      <MessageSquare className="w-4 h-4 mr-3 flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium truncate">
                          {conversation.title}
                        </p>
                        <p className="text-xs text-emerald-700 dark:text-emerald-300 truncate">
                          Shared • Owner: {conversation.owner_display || 'Unknown'}
                        </p>
                      </div>
                      <div className="flex items-center space-x-1 opacity-0 group-hover:opacity-100 transition-opacity">
                        <Button
                          variant="ghost"
                          size="icon"
                          className={`h-7 w-7 ${
                            selectedConversationId === conversation.id
                              ? 'hover:bg-white/20 text-white'
                              : 'hover:bg-gray-200 dark:hover:bg-gray-600'
                          }`}
                          onClick={(e) => {
                            e.stopPropagation()
                            if (conversation.is_owner) {
                              setConversationToRename(conversation)
                              setRenameDialogOpen(true)
                            }
                          }}
                          disabled={!conversation.is_owner}
                        >
                          <Edit2 className="w-3 h-3" />
                        </Button>
                        {/* Allow delete only for owner; shared participants should use 'leave chat' from chat header */}
                        <Button
                          variant="ghost"
                          size="icon"
                          className={`h-7 w-7 ${
                            selectedConversationId === conversation.id
                              ? 'hover:bg-white/20 text-white'
                              : 'hover:bg-red-100 dark:hover:bg-red-900/30 hover:text-red-600'
                          }`}
                          onClick={(e) => {
                            e.stopPropagation()
                            if (conversation.is_owner) {
                              onDeleteConversation(conversation.id)
                            }
                          }}
                          disabled={!conversation.is_owner}
                        >
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
      
      {/* Footer */}
      <div className="p-3 border-t bg-white/50 dark:bg-gray-800/50">
        <div className="flex items-center justify-center space-x-2 text-xs text-muted-foreground">
          <img 
            src="/giatec-logo.png" 
            alt="Giatec" 
            className="h-5 w-auto object-contain opacity-60"
            onError={(e) => e.currentTarget.style.display = 'none'}
          />
          <span>Powered by Giatec</span>
        </div>
      </div>

      {/* Rename Dialog */}
      {conversationToRename && (
        <RenameConversationDialog
          open={renameDialogOpen}
          currentTitle={conversationToRename.title}
          onOpenChange={(open) => {
            setRenameDialogOpen(open)
            if (!open) {
              setConversationToRename(null)
            }
          }}
          onConfirm={(newTitle) => {
            onRenameConversation(conversationToRename.id, newTitle)
            setRenameDialogOpen(false)
            setConversationToRename(null)
          }}
        />
      )}
    </div>
  )
}

